// A branch's own copy of a mini-app's screens (ADR 0065 §5 Q6): when the project changed its copy, cortex's page for that
// mini-app shows the copy's views instead of its own, with cortex's providers around them. The api builds the copy's
// screens once per fingerprint into one browser module; the packages that must be one instance in the page (React, the
// router, the query cache) are handed to it here as `globalThis.__cortexShared` (apps/api/src/sdk-host/copies.ts).
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as ReactDOMClient from 'react-dom/client';
import * as JsxRuntime from 'react/jsx-runtime';
import * as ReactRouter from 'react-router';
import * as ReactQuery from '@tanstack/react-query';
import { apiBaseUrl, apiFetch, getAccessToken } from './api.ts';

const shared: Record<string, unknown> = {
  react: React,
  'react/jsx-runtime': JsxRuntime,
  'react-dom': ReactDOM,
  'react-dom/client': ReactDOMClient,
  'react-router': ReactRouter,
  '@tanstack/react-query': ReactQuery,
};

export type CopyViews<T> =
  | { state: 'loading' }
  | { state: 'none' }
  | { state: 'copy'; fingerprint: string; views: T }
  | { state: 'failed'; error: string };

const loaded = new Map<string, Promise<unknown>>();

async function copyOf(
  module: string,
  slug: string,
  branch: string,
): Promise<{ fingerprint: string; views: unknown } | null> {
  const token = getAccessToken();
  // apiFetch: on a shared app's page it names the app (a person who reaches the project only through it reads there).
  const r = await apiFetch(
    `${apiBaseUrl}/api/${module}/copy-web/projects/${encodeURIComponent(slug)}/branches/${encodeURIComponent(branch)}`,
    { headers: token ? { authorization: `Bearer ${token}` } : {} },
  );
  if (!r.ok) throw new Error(`the copy's screens answered ${r.status}`);
  const { copy } = (await r.json()) as { copy: { fingerprint: string; script: string } | null };
  if (!copy) return null;
  (globalThis as { __cortexShared?: Record<string, unknown> }).__cortexShared ??= shared;
  let views = loaded.get(copy.script);
  if (!views) {
    views = import(/* @vite-ignore */ new URL(copy.script, `${apiBaseUrl}/`).toString());
    views.catch(() => loaded.delete(copy.script));
    loaded.set(copy.script, views);
  }
  return { fingerprint: copy.fingerprint, views: await views };
}

/** The copy's views of `module` for a project branch, when the branch changed its copy; `none` otherwise. */
export function useCopyViews<T>(module: string, slug: string, branch: string): CopyViews<T> {
  const [v, setV] = React.useState<CopyViews<T>>({ state: 'loading' });
  React.useEffect(() => {
    let live = true;
    setV({ state: 'loading' });
    copyOf(module, slug, branch).then(
      (c) =>
        live &&
        setV(c ? { state: 'copy', fingerprint: c.fingerprint, views: c.views as T } : { state: 'none' }),
      (e: unknown) => live && setV({ state: 'failed', error: e instanceof Error ? e.message : String(e) }),
    );
    return () => {
      live = false;
    };
  }, [module, slug, branch]);
  return v;
}

/** A copy's views that throw while rendering: the page falls back to cortex's own, saying so. */
class CopyBoundary extends React.Component<
  { fallback: (error: string) => React.ReactNode; children: React.ReactNode },
  { error: string | null }
> {
  override state = { error: null as string | null };
  static getDerivedStateFromError(e: unknown) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
  override render() {
    return this.state.error !== null ? this.props.fallback(this.state.error) : this.props.children;
  }
}

function CopyFailed({ module, error }: { module: string; error: string }) {
  return (
    <p role="alert" className="text-destructive p-2 text-sm">
      This branch's own copy of the {module} screens did not load ({error}); cortex's are shown.
    </p>
  );
}

/**
 * Renders a mini-app's page with the branch's own copy of its views when the branch changed the copy, cortex's
 * otherwise. `cortex` names the views the page uses (its provider, its page); the copy must offer each by the same
 * name, else (or when they fail) cortex's are shown with a note. Nothing renders while the branch is asked.
 */
export function OwnViews<T extends Record<string, unknown>>({
  module,
  slug,
  branch,
  cortex,
  children,
}: {
  module: string;
  slug: string;
  branch: string;
  cortex: T;
  children: (views: T, key: string) => React.ReactNode;
}) {
  const copy = useCopyViews<Record<string, unknown>>(module, slug, branch);
  if (copy.state === 'loading') return null;
  const own = (error: string) => (
    <>
      <CopyFailed module={module} error={error} />
      {children(cortex, `${slug}/${branch}`)}
    </>
  );
  if (copy.state === 'failed') return own(copy.error);
  if (copy.state === 'none') return children(cortex, `${slug}/${branch}`);
  const missing = Object.keys(cortex).filter((k) => typeof copy.views[k] === 'undefined');
  if (missing.length) return own(`it offers no ${missing.join(', ')}`);
  const views = Object.fromEntries(Object.keys(cortex).map((k) => [k, copy.views[k]])) as T;
  return (
    <CopyBoundary key={copy.fingerprint} fallback={own}>
      {children(views, `${slug}/${branch}/${copy.fingerprint}`)}
    </CopyBoundary>
  );
}
